import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { useExternalConsentsDataQuery, useRegistration } from 'hooks/webapi';
import useAuth from 'utils/auth';
import { useClientApplicationCheckStatusQuery } from 'hooks/webapi/client/useClientApplicationCheckStatusQuery';
import { isAlreadyExistErrorMsg } from 'utils/errorMessageValidation';
import {
  ValidationError,
  formatBackendValidationErrors,
  scrollToElement,
} from 'utils';
import { Consent } from 'hooks/webapi/useRegistration';
import { PERSONAL_DETAILS_CONTINUE } from 'Router/routes';
import useAffiliate from 'hooks/webapi/useAffiliate';
import useIovation from 'hooks/useIovation';
import { PersonalDetailsFormValues } from './PersonalDetailsFormValues';
import validatePersonalDetailsForm from './validatePersonalDetailsForm';

export default function usePersonalDetails() {
  const [pending, setPending] = useState(false);
  const { loginWithKeycloakLink } = useAuth();
  const { partialRegister } = useRegistration();
  const [isPollingApplicationStatus, setIsPollingApplicationStatus] =
    useState(false);
  const { data: applicationCheckStatusData } =
    useClientApplicationCheckStatusQuery(isPollingApplicationStatus, 3000);

  const [isAccountAlreadyExistsError, setIsAccountAlreadyExistsError] =
    useState(false);
  const { data: availableConsents, isLoading } = useExternalConsentsDataQuery();

  const { affiliate, affiliateForm } = useAffiliate();
  const { ioBlackBox } = useIovation();

  const handleSubmit = useCallback(
    async (
      values: PersonalDetailsFormValues,
      actions: FormikHelpers<PersonalDetailsFormValues>,
    ) => {
      try {
        setPending(true);
        // const recaptchaToken = await handleRecaptchaVerify();
        const { email, password } = values;

        const sanitizedValues: PersonalDetailsFormValues & {
          consents: Consent[];
        } = {
          ...values,
          mobilePhone: values.mobilePhone.replace(/ /g, '').replace('+420', ''),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          passwordRepeat: values.password,
          consents: [
            {
              name: 'ACCEPT_CREDIT_AGREEMENT',
              accepted: values.acceptAgreement,
              version: availableConsents?.ACCEPT_CREDIT_AGREEMENT.version,
            },
            {
              name: 'UNDERSTANDING_AGREEMENT_CONFIRMED',
              accepted: values.acceptProfiling,
              version:
                availableConsents?.UNDERSTANDING_AGREEMENT_CONFIRMED.version,
            },
            {
              name: 'ACCEPT_NEWS',
              accepted: values.acceptNews,
              version: availableConsents?.ACCEPT_NEWS.version,
            },
          ],
        };

        const clientData = await partialRegister(
          { body: sanitizedValues } /* recaptchaToken */,
        );

        const callbackUrl = new URL(
          `${window.location.protocol}//${window.location.host}${PERSONAL_DETAILS_CONTINUE}`,
        );

        const loginData = await loginWithKeycloakLink(
          email,
          password,
          clientData.clientUuid,
          callbackUrl.toString(),
        );

        setPending(false);
        if (loginData) window.location.assign(loginData.link);
      } catch (e: unknown) {
        if (
          e !== null &&
          e instanceof ValidationError &&
          e?.fieldErrors?.length
        ) {
          const isAlreadyExistError = isAlreadyExistErrorMsg(e);

          if (isAlreadyExistError) {
            setIsAccountAlreadyExistsError(true);
            window.scrollTo(0, 0);
            setPending(false);
            return;
          }

          const errors = formatBackendValidationErrors(e.fieldErrors);

          actions.setErrors(errors);
          scrollToElement(e.fieldErrors[0].property);
        }

        setPending(false);
      }
    },
    [
      availableConsents?.ACCEPT_CREDIT_AGREEMENT.version,
      availableConsents?.UNDERSTANDING_AGREEMENT_CONFIRMED.version,
      availableConsents?.ACCEPT_NEWS.version,
      partialRegister,
    ],
  );

  const handleLoginClick = useCallback((email: string) => {
    window.location.assign(
      `${import.meta.env.VITE_DASHBOARD_URL}${
        import.meta.env.VITE_DASHBOARD_LOGIN_ROUTE
      }?${import.meta.env.VITE_DASHBOARD_LOGIN_SEARCH_PARAMETER}=${window.btoa(
        email,
      )}`,
    );
  }, []);

  useEffect(() => {
    if (applicationCheckStatusData?.completed) {
      // We can stop polling the status check, when we have result
      setIsPollingApplicationStatus(false);
    }
  }, [applicationCheckStatusData]);

  const handleAlreadyExistBoxClose = useCallback(() => {
    setIsAccountAlreadyExistsError(false);
  }, []);

  const formik = useFormik({
    initialValues: {
      affiliateInfo: affiliate,
      firstName: '',
      lastName: '',
      mobilePhone: '',
      personalId: '',
      email: '',
      password: '',
      passwordRepeat: '',
      acceptAgreement: false,
      acceptProfiling: false,
      acceptNews: false,
      acceptFaceIdProcessing: false,
      source: 'DESKTOP',
      ioBlackBox: '',
    },
    onSubmit: handleSubmit,
    validate: validatePersonalDetailsForm,
  });

  useEffect(() => {
    formik.setFieldValue('ioBlackBox', ioBlackBox);
  }, [ioBlackBox]);

  useEffect(() => {
    (async () => {
      if (affiliateForm) {
        Object.keys(affiliateForm).forEach((val) =>
          formik.setFieldTouched(val, true),
        );
        await formik.setValues({
          ...formik.values,
          ...affiliateForm,
        });
      }
    })();
  }, [affiliateForm]);

  useEffect(() => {
    if (formik.isSubmitting) {
      const error = Object.entries(formik.errors).find(
        (key) => key[1] !== undefined,
      );

      if (error) {
        scrollToElement(error[0]);
      }
    }
  }, [formik.errors, formik.isSubmitting]);

  const handleCheckboxChangeWithTouched = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      formik.setFieldTouched(e.target.name);
      formik.handleChange(e);
    },
    [formik],
  );

  const showErrorFormikField = (field: keyof PersonalDetailsFormValues) =>
    formik.touched[field] && formik.errors[field];

  return {
    formik,
    handleCheckboxChangeWithTouched,
    showErrorFormikField,
    pending,
    handleSubmit,
    isAccountAlreadyExistsError,
    handleLoginClick,
    handleAlreadyExistBoxClose,
    availableConsents,
    availiableConsentsLoading: isLoading,
  };
}
